import { Box, Flex, Icon, Text } from '@chakra-ui/react'
import {
  ArrowRight,
  MessageSquareText,
  PhoneCall,
  PhoneIncoming,
  Printer,
  Voicemail,
} from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  EventHistory,
  EventHistoryStep,
} from '~/components/shared/EventHistory'
import { formatPhoneNumber } from '~/utils/formatter'

import { toNounCasing } from '@mangovoice/core'
import { formatDateString, formatTimeString } from '@mangovoice/core'

import { useContactEventStyle } from './componentStyles/ContactEvent.style'
import { useContactHistoryStyle } from './componentStyles/ContactHistory.style'
import {
  ContactHistory,
  ContactHistoryType,
} from './viewModels/Contacts.viewmodel'

const historyTypeToIconMap: Record<
  ContactHistoryType,
  React.ComponentType<any>
> = {
  messaging: MessageSquareText,
  inboundCall: PhoneIncoming,
  outboundCall: PhoneCall,
  inboundFax: Printer,
  outboundFax: Printer,
  voicemail: Voicemail,
}

interface ContactEventProps {
  eventDate: Date
  icon: React.ComponentType<any>
  label: string
  fromNumber?: string
  toNumber?: string
}
const ContactEvent = ({
  eventDate,
  icon,
  label,
  fromNumber,
  toNumber,
}: ContactEventProps) => {
  const styles = useContactEventStyle()
  return (
    <Flex css={styles.container}>
      <Text
        css={styles.dateText}
      >{`${formatDateString(eventDate)} ${formatTimeString(eventDate)}`}</Text>
      <Flex css={styles.typeContainer}>
        <Icon css={styles.typeIcon}>
          {React.createElement(icon, { size: '100%' })}
        </Icon>
        <Text css={styles.typeText}>{label}</Text>
      </Flex>
      <Flex css={styles.numbersContainer}>
        {fromNumber && (
          <>
            <Text css={styles.fromNumberText}>{fromNumber}</Text>
            <Icon as={ArrowRight} css={styles.arrowIcon} />
          </>
        )}
        {toNumber && <Text css={styles.toNumberText}>{toNumber}</Text>}
      </Flex>
    </Flex>
  )
}

interface ContactHistoryProps {
  contactHistory: ContactHistory[]
}
export const ContactEventHistory = ({
  contactHistory,
}: ContactHistoryProps) => {
  const styles = useContactHistoryStyle()
  const { t } = useTranslation()

  return (
    <Flex css={styles.container}>
      {/* Contact History Header */}
      <Flex css={styles.headerContainer}>
        <Text as="h2" css={styles.headerText}>
          {t('contactEventHistory.recentActivity')}
        </Text>
        <Text css={styles.timespanText}>
          {t('contactEventHistory.timespanText')}
        </Text>
      </Flex>
      <Box css={styles.scrollContainer} className="scrollbar-hidden">
        <EventHistory css={styles.stepperContainer}>
          {contactHistory.map((history, index) => {
            let fromNumberDisplay: string | undefined
            let toNumberDisplay: string | undefined
            if (history?.fromNumber) {
              if (typeof history?.fromNumber === 'string') {
                fromNumberDisplay = formatPhoneNumber(history.fromNumber)
              } else {
                fromNumberDisplay = `${toNounCasing(
                  history.fromNumber.label,
                )}: ${formatPhoneNumber(history.fromNumber.number)}`
              }
            }
            if (history?.toNumber) {
              if (typeof history?.toNumber === 'string') {
                toNumberDisplay = formatPhoneNumber(history.toNumber)
              } else {
                toNumberDisplay = `${toNounCasing(
                  history.toNumber.label,
                )}: ${formatPhoneNumber(history.toNumber.number)}`
              }
            }

            return (
              <EventHistoryStep key={index} index={index}>
                <ContactEvent
                  eventDate={history.date}
                  icon={historyTypeToIconMap[history.type]}
                  label={t(`contactEventHistory.historyLabel.${history.type}`)}
                  fromNumber={fromNumberDisplay}
                  toNumber={toNumberDisplay}
                />
              </EventHistoryStep>
            )
          })}
        </EventHistory>
      </Box>
    </Flex>
  )
}
